import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { Button, cn } from '@ludus/ui'
import type { PipelineId } from '@ludus/core'
import {
  NODE_SPECS,
  isKnownNodeType,
  nodeRole,
  rowsToRule,
  ruleToRows,
  withNodeDefaults,
  type FlowGraph,
  type FlowNode,
  type NodeField,
  type RuleRow,
} from '@/features/flows/types.ts'
import { nodeTypeKey } from '@/features/flows/Canvas/useNodeLabels.ts'
import { useFlowEditorState } from '@/features/flows/Canvas/useFlowEditorState.ts'
import { FieldControl } from './FieldControl.tsx'
import { RuleBuilder } from './RuleBuilder.tsx'

import { TriggerSentence } from './TriggerSentence.tsx'
import { chainPathsFor } from './dataChain.ts'
import { usePipelineChoices } from './useDrawerOptions.ts'

/**
 * The options form for the selected node, generated from its own spec.
 *
 * **One component, four drawers.** `NODE_SPECS[type].fields` is the whole
 * description — kind, requiredness, whether a template is legal, which option
 * holds the board a picker should scope to — so trigger, condition,
 * `create_lead` and `update_lead` are the same code reading four field lists.
 * That is the one Directus editor idea worth keeping (`defineOperationApp`'s
 * `options: Field[]`), and it is why a fifth node type will need a spec and two
 * locale rows rather than a fifth form.
 *
 * **Edits are written through, not staged.** Every change goes straight to the
 * draft via `setNodeOptions`; only *seeding* stays local. Staging until a Done
 * button looked tidier and lost work: the drawer is keyed on the selected node,
 * so clicking another card — or the pane, or the X — unmounted it and took the
 * unsaved options with it, silently and with `dirty` still false. Opening a
 * node to read it still marks nothing, because seeding never writes; only an
 * actual edit does.
 *
 * A first edit also commits the spec's declared defaults along with it, which
 * is the same thing `addNode` does at birth — an option a person never opened
 * still has to mean what `NodeSpec.parse` will read.
 *
 * **Validation is the spec's, not a second opinion.** `NodeSpec.validate` runs
 * on the staged options and its issues are drawn against the fields they name;
 * the save's own `validateFlowGraph` still runs on the draft, and the drawer
 * never blocks a Done. A node a person is midway through building is a state
 * they pass through, and D9 marks those rather than refusing them.
 */

interface NodeDrawerProps {
  node: FlowNode
  graph: FlowGraph
  pipelineId: PipelineId
  onClose: () => void
}

export function NodeDrawer({ node, graph, pipelineId, onClose }: NodeDrawerProps) {
  const { t } = useTranslation()
  const setNodeOptions = useFlowEditorState((s) => s.setNodeOptions)
  const pipelines = usePipelineChoices()

  const known = isKnownNodeType(node.type)
  const spec = known ? NODE_SPECS[node.type] : null
  const fields: readonly NodeField[] = spec?.fields ?? []

  // The node's own options, with the spec's declared defaults filled in for
  // display. Derived rather than held, so there is no second copy to lose.
  const options = useMemo(
    () => ({ ...withNodeDefaults(node.type, node.options) }) as Record<string, unknown>,
    [node.type, node.options],
  )

  const [rawRule, setRawRule] = useState(false)
  // The raw box keeps its own text while it is open. A controlled textarea whose
  // value was re-derived from the parsed rule reverted every keystroke that left
  // the document unparseable — which is every intermediate state of editing
  // JSON — so the escape hatch for a rule the builder cannot express could only
  // be used by pasting a whole valid document.
  const [ruleText, setRuleText] = useState<string | null>(null)
  const ruleField = fields.find((f) => f.type === 'rule')
  const ruleRows = useMemo(
    () => (ruleField === undefined ? null : ruleToRows(options[ruleField.name])),
    [ruleField, options],
  )

  const paths = useMemo(() => chainPathsFor(graph), [graph])
  const targetPipelines = useMemo(
    () => pipelines.choices.filter((p) => p.id !== String(pipelineId)),
    [pipelines.choices, pipelineId],
  )

  const issues = useMemo(() => (spec === null ? [] : spec.validate(options)), [spec, options])
  const issueFor = (name: string): string | undefined => {
    const found = issues.find((i) => i.field === name)
    if (found === undefined) return undefined
    return t(`pages.flows.drawer.issue.${found.code}`, { defaultValue: found.message })
  }

  const set = (name: string, value: unknown): void =>
    setNodeOptions(node.id, { ...options, [name]: value })

  const role = known ? nodeRole(node.type) : null

  return (
    <aside
      role="dialog"
      aria-label={t('pages.flows.drawer.title', {
        type: t(`pages.flows.canvas.type.${nodeTypeKey(node.type)}`, { defaultValue: node.type }),
        defaultValue: '{{type}} options',
      })}
      className={cn(
        'flex w-full flex-col border-l border-border bg-background-secondary',
        '@lg/flow:w-96 @lg/flow:shrink-0',
      )}
    >
      <header className="flex items-center gap-2 border-b border-border px-3 py-2">
        <span className="truncate text-xs font-semibold uppercase tracking-[0.12em] text-foreground/80">
          {t(`pages.flows.canvas.type.${nodeTypeKey(node.type)}`, { defaultValue: node.type })}
        </span>
        <span className="ml-auto truncate font-mono text-[10px] text-muted">{node.key}</span>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          aria-label={t('pages.flows.drawer.close', { defaultValue: 'Close' })}
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-3 py-3">
        {spec === null && (
          <p className="text-xs italic text-muted">
            {t('pages.flows.drawer.unknownType', {
              type: node.type,
              defaultValue: 'This build has no editor for a "{{type}}" node.',
            })}
          </p>
        )}

        {role === 'trigger' && (
          <TriggerSentence
            node={node}
            options={options}
            pipelineId={pipelineId}
            onChange={set}
            issueFor={issueFor}
          />
        )}

        {role !== 'trigger' &&
          fields.map((field) => {
            if (field.type === 'rule') return null
            const issue = issueFor(field.name)
            return (
              <label key={field.name} className="flex flex-col gap-1">
                <span className="text-[11px] uppercase tracking-wide text-muted">
                  {t(`pages.flows.canvas.option.${field.name}`, { defaultValue: field.name })}
                  {field.required && <span aria-hidden="true"> *</span>}
                </span>
                <FieldControl
                  field={field}
                  value={options[field.name]}
                  options={options}
                  ownPipelineId={pipelineId}
                  paths={paths}
                  targetPipelines={targetPipelines}
                  onChange={(value) => set(field.name, value)}
                />
                {issue !== undefined && (
                  <span className="text-[11px] text-toast-error-text">{issue}</span>
                )}
              </label>
            )
          })}

        {ruleField !== undefined && (
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-2">
              <span className="text-[11px] uppercase tracking-wide text-muted">
                {t(`pages.flows.canvas.option.${ruleField.name}`, {
                  defaultValue: ruleField.name,
                })}
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto text-[11px]"
                onClick={() => {
                  setRuleText(null)
                  setRawRule((p) => !p)
                }}
              >
                {rawRule
                  ? t('pages.flows.drawer.rule.builder', { defaultValue: 'Builder' })
                  : t('pages.flows.drawer.rule.raw', { defaultValue: 'Raw JSON' })}
              </Button>
            </div>

            {rawRule || ruleRows === null ? (
              <>
                {ruleRows === null && !rawRule && (
                  <p className="text-[11px] text-muted">
                    {t('pages.flows.drawer.rule.tooComplex', {
                      defaultValue:
                        'This rule is a shape the builder cannot edit, so it is shown as written.',
                    })}
                  </p>
                )}
                <textarea
                  value={ruleText ?? JSON.stringify(options[ruleField.name] ?? {}, null, 2)}
                  onChange={(e) => {
                    setRuleText(e.target.value)
                    try {
                      set(ruleField.name, JSON.parse(e.target.value) as unknown)
                    } catch {
                      // The text stands while it is not yet valid JSON; the rule
                      // keeps its last good value, so a half-typed document
                      // cannot destroy the one that was there.
                    }
                  }}
                  rows={8}
                  className="rounded-md border border-border bg-background px-2 py-1.5 font-mono text-[11px]"
                />
              </>
            ) : (
              <RuleBuilder
                rows={ruleRows}
                onChange={(rows: RuleRow[]) => set(ruleField.name, rowsToRule(rows))}
                pipelines={targetPipelines}
                paths={paths.map((p) => p.path)}
              />
            )}
            {issueFor(ruleField.name) !== undefined && (
              <span className="text-[11px] text-toast-error-text">{issueFor(ruleField.name)}</span>
            )}
          </div>
        )}
      </div>

      <footer className="flex items-center gap-2 border-t border-border px-3 py-2">
        {issues.length > 0 && (
          <span className="text-[11px] text-muted">
            {t('pages.flows.drawer.issues', {
              count: issues.length,
              defaultValue: '{{count}} to fix',
            })}
          </span>
        )}
        {/* Closes the drawer and nothing else — every edit is already in the
            draft, so this is not a commit and must not read as one. */}
        <Button variant="primary" size="sm" className="ml-auto text-xs" onClick={onClose}>
          {t('pages.flows.drawer.done', { defaultValue: 'Done' })}
        </Button>
      </footer>
    </aside>
  )
}
